import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  Send, 
  MessageSquare, 
  User,
  Bot
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useUnreadMessages } from "@/hooks/useUnreadMessages";
import { markMessagesAsRead } from "@/lib/messageUtils";
import { toast } from "sonner";

interface Message {
  id: string;
  message: string;
  sender_id: string;
  receiver_id: string;
  created_at: string;
  is_read: boolean;
  sender: {
    display_name: string;
    avatar_url?: string;
    user_type: string;
  };
}

interface GameChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  game: {
    id: string;
    title: string;
  };
  developer: {
    id: string;
    display_name: string;
    avatar_url?: string;
  };
  currentUserId: string;
}

export const GameChatModal = ({ 
  isOpen, 
  onClose, 
  game, 
  developer,
  currentUserId 
}: GameChatModalProps) => {
  const { refetch: refetchUnreadCount } = useUnreadMessages();
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Vérification de sécurité pour éviter les erreurs null
  if (!game || !developer) {
    return null;
  }

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (isOpen && game?.id) {
      fetchMessages();
    }
  }, [isOpen, game?.id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchMessages = async () => {
    if (!game?.id || !currentUserId) return;
    
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("messages")
        .select(`
          *,
          sender:profiles!messages_sender_id_fkey(
            display_name,
            avatar_url,
            user_type
          )
        `)
        .eq("game_id", game.id)
        .or(`sender_id.eq.${currentUserId},receiver_id.eq.${currentUserId}`)
        .order("created_at", { ascending: true });

      if (error) {
        console.error("Error fetching messages:", error);
        toast.error("Failed to load messages: " + error.message);
        return;
      }

      setMessages(data || []);
      
      // Marquer les messages comme lus quand on ouvre le chat
      const unreadMessages = data?.filter(msg => msg.receiver_id === currentUserId && !msg.is_read) || [];
      if (unreadMessages.length > 0) {
        const messageIds = unreadMessages.map(msg => msg.id);
        const success = await markMessagesAsRead(messageIds, currentUserId, () => {
          // Mettre à jour le compteur immédiatement
          refetchUnreadCount();
        });
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("An error occurred while loading messages");
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || sending || !game?.id || !developer?.id || !currentUserId) return;

    try {
      setSending(true);
      const { error } = await supabase
        .from("messages")
        .insert({
          game_id: game.id,
          sender_id: currentUserId,
          receiver_id: developer.id,
          message: newMessage.trim()
        });

      if (error) {
        console.error("Error sending message:", error);
        toast.error("Failed to send message: " + error.message);
        return;
      }

      setNewMessage("");
      await fetchMessages(); // Refresh messages
      refetchUnreadCount(); // Mettre à jour le compteur
    } catch (error) {
      console.error("Error:", error);
      toast.error("An error occurred while sending message");
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            Chat - {game.title}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            Chat with {developer.display_name} about this game
          </p>
        </DialogHeader>

        <div className="flex-1 flex flex-col min-h-0">
          {/* Messages */}
          <div className="flex-1 overflow-y-auto space-y-4 p-4 bg-muted/20 rounded-lg">
            {loading ? (
              <div className="text-center text-muted-foreground">
                Loading messages...
              </div>
            ) : messages.length === 0 ? (
              <div className="text-center text-muted-foreground">
                No messages yet. Start the conversation!
              </div>
            ) : (
              messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex gap-3 ${
                    message.sender_id === currentUserId ? "flex-row-reverse" : "flex-row"
                  }`}
                >
                  <Avatar className="w-8 h-8 flex-shrink-0">
                    <AvatarImage src={message.sender.avatar_url || ""} />
                    <AvatarFallback>
                      {message.sender.user_type === 'developer' ? (
                        <Bot className="w-4 h-4" />
                      ) : (
                        <User className="w-4 h-4" />
                      )}
                    </AvatarFallback>
                  </Avatar>
                  <div
                    className={`max-w-[70%] ${
                      message.sender_id === currentUserId
                        ? "bg-primary text-primary-foreground"
                        : "bg-background border"
                    } rounded-lg px-3 py-2`}
                  >
                    <div className="text-sm font-medium mb-1">
                      {message.sender.display_name}
                    </div>
                    <div className="text-sm whitespace-pre-wrap">
                      {message.message}
                    </div>
                    <div className="text-xs opacity-70 mt-1">
                      {formatTime(message.created_at)}
                    </div>
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Message Input */}
          <div className="flex gap-2 mt-4">
            <Input
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message..."
              disabled={sending}
              className="flex-1"
            />
            <Button
              onClick={sendMessage}
              disabled={!newMessage.trim() || sending}
              size="sm"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
